import * as React from 'react';
import * as XLSX from 'xlsx';
import {
  PageWrapper,
  H5,
  Tableheader,
  Tabletitle,
  Table,
  Thead,
  ColSpan1,
  ColSpan2,
  Tablerow,
  ColSpan4,
} from './styles';
import { UpperTittleWrap, H2 } from '../signInvigilatorRecords/styles';
import { AdminHomeLoader, NoData } from '../../../../../components';
import { useQuery, useMutation } from '@apollo/client';
import { GET_MISSING_COURSE_REPORTS, RESOLVE_MISSING_COURSE_REPORT } from './graphql';
import { toast } from 'react-toastify';
import { useExamsTypeAndSem } from '../../../../../helpers/examTypeAndSemester';
import { academicYear } from '../../../../../helpers';



interface MissingCourse {
  courseId: string;
  courseName: string;
  courseCode: string;
}

interface MissingCourseReport {
  indexNumber: string;
  name: string;
  missingCourses: MissingCourse[];
}

interface GetMissingCourseReportsQuery {
  getMissingCoursesReport: MissingCourseReport[];
}


export const MissingCourseReport = (): JSX.Element => {
  const { loading, data, error, refetch } =
    useQuery<GetMissingCourseReportsQuery>(GET_MISSING_COURSE_REPORTS);

  const examsTypeAndSemester = useExamsTypeAndSem();

  const [resolveReport, { loading: resolving }] = useMutation(
    RESOLVE_MISSING_COURSE_REPORT,
    {
      onCompleted: (res) => {
        if (res?.resolveMissingCourseReport?.status) {
          toast.success(
            res.resolveMissingCourseReport.message ?? 'Report resolved successfully.'
          );
          refetch();
        } else {
          toast.error(
            res?.resolveMissingCourseReport?.message ?? 'Failed to resolve report.'
          );
        }
      },
      onError: () => toast.error('An error occurred while resolving the report.'),
    }
  );


  if (loading) return <AdminHomeLoader />;


  const handleExportExcel = () => {
    if (error || !data?.getMissingCoursesReport || data.getMissingCoursesReport.length === 0) {
      toast.error('You can only export report when data exists!');
      return;
    }
 
    const rows: Record<string, string | number>[] = [];
    let sn = 1;
    data.getMissingCoursesReport.forEach((report) => {
      report.missingCourses.forEach((course) => {
        rows.push({
          SN: sn++,
          'Index Number': report.indexNumber,
          Name: report.name,
          'Course Code': course.courseCode,
          'Course Name': course.courseName,
        });
      });
    });
 
    const worksheet = XLSX.utils.json_to_sheet(rows);
 
    worksheet['!cols'] = [
      { wch: 5 },  // SN
      { wch: 15 }, // Index Number
      { wch: 35 }, // Name
      { wch: 12 }, // Course Code
      { wch: 40 }, // Course Name
    ];
 
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Missing Courses');
 
    const filename = `${academicYear}-${examsTypeAndSemester?.examType}-${examsTypeAndSemester?.semester}-missing-course-reports.xlsx`;
    XLSX.writeFile(workbook, filename);
  };
 



  const handleResolve = (indexNumber: string) => {
    resolveReport({ variables: { indexNumber } });
  };

  const hasData =
    !error &&
    data?.getMissingCoursesReport &&
    data.getMissingCoursesReport.length > 0;



  return (
    <PageWrapper>
      <div className="flex items-center">
        <UpperTittleWrap>
          <H2>Current</H2>
        </UpperTittleWrap>
        <H5>Archives</H5>
      </div>

      <Tableheader>
        <div>
          <Tabletitle>Missing Course Reports</Tabletitle>
        </div>

        <div>
                    <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-6 py-2 font-semibold
              bg-green-700 hover:bg-green-800 text-white rounded-md
              transition-colors duration-150"
            style={
              !data?.getMissingCoursesReport || error
                ? { cursor: 'not-allowed', opacity: 0.6 }
                : { cursor: 'pointer' }
            }
          >
            Export to Excel
          </button>
        </div>
      </Tableheader>

      <Table>
        <Thead key="header">
          <ColSpan1>SN</ColSpan1>
          <ColSpan2>Index No.</ColSpan2>
          <ColSpan4>Name</ColSpan4>
          <ColSpan2>Course Code</ColSpan2>
          <ColSpan4>Course Name</ColSpan4>
          <div className="flex-1 text-center font-semibold text-sm">Action</div>
        </Thead>

        {/* Body */}
        {!hasData ? (
          <NoData title="No missing course reports found!" />
        ) : (
          <div className="flex-1 h-[36rem] 2xl:h-[42rem] 2xl:pb-30 pb-96 overflow-y-auto">
            {data!.getMissingCoursesReport.map((report, reportIndex) =>
              report.missingCourses.map((course, courseIndex) => (
                <Tablerow key={`${report.indexNumber}-${course.courseId}`}>
                  <ColSpan1>
                    {courseIndex === 0
                      ? reportIndex + 1
                      : ''}
                  </ColSpan1>

                  <ColSpan2>{report.indexNumber}</ColSpan2>
                  <ColSpan4>{report.name}</ColSpan4>
                  <ColSpan2>{course.courseCode}</ColSpan2>
                  <ColSpan4>{course.courseName}</ColSpan4>

                  <div className="flex-1 flex items-center justify-center">
                    {courseIndex === 0 ? (
                      <button
                        disabled={resolving}
                        onClick={() => handleResolve(report.indexNumber)}
                        className="px-4 py-1 text-xs font-semibold rounded-md
                          bg-green-500 hover:bg-green-600 text-white
                          disabled:opacity-50 disabled:cursor-not-allowed
                          transition-colors duration-150"
                      >
                        {resolving ? 'Resolving…' : 'Resolve'}
                      </button>
                    ) : null}
                  </div>
                </Tablerow>
              ))
            )}
          </div>
        )}
      </Table>
    </PageWrapper>
  );
};